import React, { useState, useEffect } from 'react';
import { useTransparencyDetection } from '../../../hooks/useTransparencyDetection';

const ImageViewer = ({ src, filename, fileSize, isDarkMode }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);

  // Reset loaded state when image source changes (e.g., navigating between images)
  useEffect(() => {
    setLoaded(false);
  }, [src]);

  // Detect transparency for checkerboard background indicator
  const { hasTransparency } = useTransparencyDetection(loaded ? src : null, filename);

  // Handle wheel event for zoom
  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoomLevel(prev => Math.min(prev + 0.1, 3));
    } else {
      setZoomLevel(prev => Math.max(prev - 0.1, 1));
      if (zoomLevel <= 1.1) {
        setPosition({ x: 0, y: 0 });
      }
    }
  };

  // Handle double-click for zoom
  const handleDoubleClick = (e) => {
    e.stopPropagation();
    if (zoomLevel > 1) {
      setZoomLevel(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setZoomLevel(2);
      const rect = e.currentTarget.getBoundingClientRect();
      const x = (e.clientX - rect.left) - (rect.width / 2);
      const y = (e.clientY - rect.top) - (rect.height / 2);
      setPosition({ x: -x, y: -y });
    }
  };

  // Dragging functions for panning when zoomed
  const handleMouseDown = (e) => {
    e.stopPropagation();
    if (zoomLevel > 1) {
      setIsDragging(true);
      setStartPos({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPosition({
        x: e.clientX - startPos.x,
        y: e.clientY - startPos.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const showCheckerboard = loaded && hasTransparency;
  const imageClass = `full-page-image${showCheckerboard ? ' has-transparency-bg' : ''}`;

  return (
    <div 
      className="image-container"
      onWheel={handleWheel}
      onDoubleClick={handleDoubleClick}
      style={{ 
        cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        overflow: 'hidden'
      }}
    >
      <div
        className="image-wrapper"
        style={{
          transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
          transformOrigin: 'center center'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <img 
          src={src} 
          alt={filename || "Media content"}
          className={imageClass}
          draggable={false}
          onLoad={() => setLoaded(true)}
        />
      </div>
    </div>
  );
};

export default ImageViewer;
